import React, { Fragment, useEffect, useState } from "react";
import Select from "react-select";
import { treeData } from "../mapServices";
import { Link, useNavigate } from "react-router-dom";
import HomepageCarousel from "components/HomepageCarousel/HomepageCarousel";
import Hero from "../Hero/Hero.jsx";
import "./Home.css";
import { getAllstates } from "../../services/pestTableService";
import {
  getHostsByState,
  getPestsByStateAndHost,
} from "../../services/mapService";
import spbScreenshot from "assets/img/spb.png";
import feScreenshot from "assets/img/fe.png";
import eabScreenshot from "assets/img/eab.png";

const Home = () => {
  const [selectedState, setSelectedState] = useState();
  const [selectedPest, setSelectedPest] = useState();
  const [selectedTree, setSelectedTree] = useState();
  const [stateList, setStateList] = useState();
  const [treeList, setTreeList] = useState();
  const [pestList, setPestList] = useState();
  const [announcement, setAnnouncement] = useState("");
  const navigate = useNavigate();
  const esriPublisherID = "gGHDlz6USftL5Pau";
  const esriLayerName = "FPC_Centroids";

  // If selected state changes
  useEffect(() => {
    if (selectedState) {
      getHostsByState(esriPublisherID, esriLayerName, selectedState.label).then(
        (result) => {
          const treeOptions = result.data.features;
          const cleanedTreeList = cleanTreeList(treeOptions);
          setTreeList(cleanedTreeList);
          setAnnouncement("Tree Species/Group selector is now available");
          setSelectedTree("");
          setSelectedPest("");
        }
      );
    }
  }, [selectedState]);

  // If selected tree changes
  useEffect(() => {
    if (selectedTree) {
      getPestsByStateAndHost(
        esriPublisherID,
        esriLayerName,
        selectedState.label,
        selectedTree.label
      ).then((result) => {
        const pestOptions = result.data.features;
        const cleanedPestList = cleanPestList(pestOptions);
        setPestList(cleanedPestList);
        setAnnouncement("Pest Species selector is now available");
        setSelectedPest("");
      });
    }
  }, [selectedTree]);

  // If selected pest changes
  useEffect(() => {
    if (selectedPest) {
      setAnnouncement("Search button is now available");
    }
  }, [selectedPest]);

  // Initial load
  useEffect(() => {
    window.scroll(0, 0);
    setAnnouncement("You are on the Forest Pest Conditions homepage");
    getAllstates().then((result) => {
      const allStates = { value: 0, label: "All States", isDisabled: false };
      const stateOptions = result.data;
      stateOptions.unshift(allStates);
      setStateList(Array.from(stateOptions));
    });
    document.title = "Forest Pest Conditions - Home Page";
  }, []);

  const cleanTreeList = (input) => {
    const allTrees = {
      label: "All Trees",
      value: "",
    };
    const treeArray = [];
    for (let value of input) {
      if (
        treeData[value.attributes.Host_Categ] ||
        value.attributes.Host_Categ === "ohia spp."
      ) {
        const newTreeItem = {
          label: value.attributes.Host_Categ,
          value: treeData[value.attributes.Host_Categ].layerURL,
          isDisabled: false,
        };
        treeArray.push(newTreeItem);
      }
    }
    treeArray.unshift(allTrees);
    return treeArray;
  };

  const cleanPestList = (input) => {
    const allPests = {
      label: "All Pests",
      value: "",
    };
    const pestArray = [];
    for (let value of input) {
      const newPestItem = {
        label: value.attributes.DCA_COMMON,
        value: value.attributes.DCA_CODE,
        isDisabled: false,
      };
      pestArray.push(newPestItem);
    }
    pestArray.unshift(allPests);
    return pestArray;
  };

  const mapSearchHandler = () => {
    setAnnouncement(
      "Now taking you to the interactive map to explore data for your selected pest"
    );
    let urlParameters = "/map/?";
    urlParameters += "state=" + selectedState.label;
    if (selectedTree) {
      urlParameters += "&tree=" + encodeURIComponent(selectedTree.label);
    }
    if (selectedPest) {
      urlParameters += "&pest=" + encodeURIComponent(selectedPest.label);
    }
    navigate(urlParameters);
  };

  return (
    <>
      <Hero value="Forest Pest Conditions" />
      <main className="grid-container homepage" id="main-content">
        <h1 id="main-content" className="main-heading">
          Monitoring the Health of America's Forests
        </h1>
        <p>
          Insects, diseases, and abiotic agents (such as drought, fire,
          flooding, salt, and wind) play critical roles in maintaining healthy,
          resilient ecosystems, but they are also among the most severe
          environmental and economic threats to the millions of acres of forests
          and urban treed areas in America. Stressors will continue to influence
          the establishment, spread, and impacts of insects and diseases on
          forests. Tree species may be positively or negatively impacted by
          these changes, altering the array of products and services derived
          from forested lands, including timber, recreation, clean water,
          energy, wildlife habitat, and jobs. Abiotic damage can also predispose
          trees and ecosystems to future biotic damage agents by causing stress
          and vulnerability and altering competition dynamics.
        </p>
        <p>
          To understand how conditions are changing and to protect species,
          forests are surveyed for insect and disease extent and intensity so
          that Federal and State agencies and other stakeholders can prioritize
          future actions and ensure forests remain resilient and sustainable
          into the future. The overall mortality caused by insects and diseases
          varies by year and by pest.
        </p>
        <hr className="mobile-disappear" />
        <div className="grid-row">
          <h2>Explore Forest Pest Impacts - Interactive Map</h2>
          <div>
            When exploring forest impacts by pest, each agent is reported
            separately. However, multiple pests may be active in the same area
            causing mortality to multiple host trees, magnifying the change in
            forest condition and creating complex forest management challenges.
          </div>
        </div>
        <div className="grid-row map-input-container">
          <div className="visually-hidden" aria-live="polite">
            {announcement}
          </div>
          <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
            {stateList && (
              <Fragment>
                <label
                  id="aria-state-label"
                  className="map-input-label"
                  htmlFor="select-state"
                >
                  State
                </label>
                <Select
                  id="select-state"
                  aria-label="Select a state. Press the down arrow to expand the option list. Press delete
                  to clear the current selection."
                  className="react-select select-state"
                  placeholder="Select a State"
                  name="singleSelect"
                  value={selectedState}
                  onChange={(value) => setSelectedState(value)}
                  options={stateList}
                  hideselectedoptions="true"
                  isClearable={true}
                  styles={{
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: "#1b1b1b",
                      tabIndex: "-1",
                    }),
                  }}
                />
              </Fragment>
            )}
          </div>
          <div className="tablet:grid-col-4 mobile-lg:grid-col-12">
            {selectedState && treeList && (
              <Fragment>
                <label
                  id="aria-tree-label"
                  className="map-input-label"
                  htmlFor="select-tree"
                >
                  Tree Species/Group
                </label>
                <Select
                  id="select-tree"
                  aria-label="Select a tree species or group. Press the down arrow to expand the option list. Press delete
                  to clear the current selection."
                  className="react-select select-tree"
                  placeholder="Select a Tree Species"
                  name="singleSelect"
                  value={selectedTree}
                  onChange={(value) => setSelectedTree(value)}
                  options={treeList}
                  hideselectedoptions="true"
                  isClearable={true}
                  styles={{
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: "#1b1b1b",
                    }),
                  }}
                />
              </Fragment>
            )}
          </div>
          <div className="tablet:grid-col-4 mobile-lg:grid-col-12">
            {selectedState && selectedTree && treeList && pestList && (
              <Fragment>
                <label
                  id="aria-pest-label"
                  className="map-input-label"
                  htmlFor="select-pest"
                >
                  Pest Species
                </label>
                <Select
                  id="select-pest"
                  aria-label="Select a pest species. Press the down arrow to expand the option list. Press delete
                  to clear the current selection."
                  className="react-select select-pest"
                  placeholder="Select a Pest Species"
                  name="singleSelect"
                  value={selectedPest}
                  onChange={(value) => setSelectedPest(value)}
                  options={pestList}
                  hideselectedoptions="true"
                  isClearable={true}
                  styles={{
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: "#1b1b1b",
                    }),
                  }}
                />
              </Fragment>
            )}
          </div>
          <div className="tablet:grid-col-1 mobile-lg:grid-col-12">
            {selectedState &&
              selectedTree &&
              selectedPest &&
              treeList &&
              pestList && (
                <Fragment>
                  <button
                    className="usa-button map-input-search"
                    onClick={mapSearchHandler}
                  >
                    Search
                  </button>
                </Fragment>
              )}
          </div>
        </div>
        <div className="grid-row">
          <b>
            View pre-filtered maps of highly impactful pests by selecting a
            region below.
          </b>
        </div>
        <br />
        <div className="grid-row">
          <div className="tablet:grid-col-4 mobile-lg:grid-col-12 text-center preselected-map">
            <img
              src={spbScreenshot}
              width="90%"
              alt="Preselected map for Southern pine beetle"
              className="preselected-map-image"
            />
            <div>
              <Link
                className="usa-link"
                to="/map/?state=All%20States&tree=pine&pest=southern%20pine%20beetle"
              >
                <strong>East Central - Southern Pine Beetle</strong>
              </Link>
            </div>
          </div>
          <div className="tablet:grid-col-4 mobile-lg:grid-col-12 text-center preselected-map">
            <img
              src={feScreenshot}
              width="90%"
              alt="Preselected map for fir engraver"
              className="preselected-map-image"
            />
            <div>
              <Link
                className="usa-link"
                to="/map/?state=All%20States&tree=fir%20spp.&pest=fir%20engraver"
              >
                <strong>West - Fir Engraver</strong>
              </Link>
            </div>
          </div>
          <div className="tablet:grid-col-4 mobile-lg:grid-col-12 text-center preselected-map">
            <img
              src={eabScreenshot}
              width="90%"
              alt="Preselected map for emerald ash borer"
              className="preselected-map-image"
            />
            <div>
              <Link
                className="usa-link"
                to="/map/?state=All%20States&tree=ash%20spp.&pest=emerald%20ash%20borer"
              >
                <strong>National - Emerald Ash Borer</strong>
              </Link>
            </div>
          </div>
        </div>
        <hr className="mobile-disappear" />
        <div className="grid-row">
          <h2>Pests of National Concern</h2>
          <div>
            Forest Health Protection programs aim to promote the health,
            diversity, and productivity of U.S. forests through effective
            management, public partnerships, and outreach efforts. Pest survey,
            monitoring, and detection activities help inform forest pest
            management decisions.{" "}
            <a href="/pest-information" className="usa-link">
              View All &raquo;
            </a>
          </div>
        </div>
        <HomepageCarousel />
        <br />
      </main>
    </>
  );
};

export default Home;
